/**
 * Company Search: Company Intelligence Brief (CIB Layer 1), appended after
 * sealed element h on the resolved-company view.
 *
 * Four states, all honest:
 *   idle        no brief has been asked for in this session. No deployed
 *               contract maps a company to an existing brief, so a cached
 *               brief is discoverable only by asking; the request control is
 *               therefore also the cache probe, and its copy says so.
 *   generating  the server acknowledged a background generation. Elapsed time
 *               since the server recorded the request is shown as elapsed
 *               time; the contract reports no progress, so none is drawn.
 *   ready       the research body verbatim, the server's generation stamp, and
 *               the regenerate control.
 *   failed      the provider's verbatim error body. No automatic retry, and
 *               the request control returns to available.
 *
 * Sealed CIB slate: value tier renders nowhere in this surface in any form, no
 * Ankura-internal financial information appears, and only fields present in the
 * response are rendered. Departed consultants and contact PII are excluded
 * server-side and nothing here adds them back. bdse_context is assembled live
 * by the backend on every call and is deliberately rendered by no panel here;
 * the resolved-company view above already renders that intelligence from its
 * own sealed contracts.
 */

import { useEffect, useState } from "react";
import { briefHandleFor, briefHandleKey, useCompanyBrief, useGenerateCompanyBrief } from "../hooks";
import { briefElapsedLabel, briefStampLabel } from "../logic";
import { BriefDocument } from "./BriefDocument";
import { IconChevron } from "./icons";

/** Named in the regenerate confirm, mirroring the R27 sweep-confirm pattern. */
const PAID_APIS = ["Anthropic", "Web search"] as const;

/**
 * The provider's failure body, collapsed by default, in the mono register.
 *
 * The backend stores provider_error unbounded and slices nothing, and this
 * surface slices nothing either: the block scrolls rather than truncating. So
 * there is no truncation, and no truncation marker is shown; marking one would
 * report a cut that never happened.
 */
function BriefErrorBody({ body }: { body: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="sxco-brief-errbody">
      <button
        type="button"
        className="sxco-brief-errtoggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={`sxco-brief-errchev${open ? " sxco-brief-errchev--open" : ""}`}>
          <IconChevron />
        </span>
        Provider response
      </button>
      {open && <pre className="sxco-brief-errpre">{body}</pre>}
    </div>
  );
}

export function CompanyBrief({
  organizationId,
  companyName,
}: {
  organizationId: string | null;
  companyName: string | null;
}): JSX.Element {
  const handleKey = briefHandleKey(organizationId, companyName);
  // Seeded from the in-memory handle map, so returning to a company the
  // operator already asked about re-checks that generation exactly once
  // (react-query's refetchOnMount) instead of starting from idle.
  const [briefId, setBriefId] = useState<string | null>(() => briefHandleFor(handleKey));
  const [confirming, setConfirming] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const generate = useGenerateCompanyBrief(handleKey, organizationId);
  const brief = useCompanyBrief(briefId);

  const view = brief.data ?? null;
  const phase = view?.phase ?? "idle";
  // A known handle whose read has not landed yet: honest "checking", not idle.
  const rechecking = Boolean(briefId) && !view && brief.isPending;

  // Live elapsed ticker, generating only. It counts real time since the server
  // recorded the request; it is not a progress indicator and never completes.
  useEffect(() => {
    if (phase !== "generating") return;
    const timer = setInterval(() => setNowMs(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [phase]);

  const request = (regenerate: boolean) => {
    if (!companyName) return;
    generate.mutate({ companyName, regenerate }, { onSuccess: (next) => setBriefId(next.briefId) });
  };

  const stamp = briefStampLabel(view?.generatedAt ?? null);
  const elapsed = briefElapsedLabel(view?.requestedAt ?? null, nowMs);
  const busy = generate.isPending;

  const exportPdf = () => {
    const previousTitle = document.title;
    const cleanup = () => {
      document.body.classList.remove("cib-printing");
      document.title = previousTitle;
    };
    document.body.classList.add("cib-printing");
    document.title = `${companyName ?? "Company"} — Company Intelligence Brief`;
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
    // `afterprint` is the normal cleanup path. The long fallback prevents a
    // stale document title if a browser suppresses that event without
    // dismantling the print layout while its dialog is still open.
    window.setTimeout(cleanup, 300_000);
  };

  // The head's activation slot. Regenerate is confirm-gated; the first request
  // is not, because it may simply return an already-generated brief.
  const activation = (() => {
    if (!companyName || confirming) return null;
    if (phase === "generating" || rechecking) return null;
    if (phase === "ready") {
      return (
        <>
          <button type="button" className="sx-btn sx-btn-p2" onClick={exportPdf}>
            Export PDF
          </button>
          <button
            type="button"
            className="sx-btn sx-btn-p3"
            onClick={() => setConfirming(true)}
            disabled={busy}
          >
            Regenerate
          </button>
        </>
      );
    }
    return (
      <button
        type="button"
        className="sx-btn sx-btn-hero"
        onClick={() => request(false)}
        disabled={busy}
      >
        {busy ? "Requesting…" : "Generate brief"}
      </button>
    );
  })();

  return (
    <section className="sxco-section">
      <div className="sxco-brief">
        <div className="sxco-brief-head">
          <div className="sxco-brief-ident">
            <span className="sxco-eyebrow">COMPANY INTELLIGENCE BRIEF</span>
            {phase === "generating" && (
              <span className="sxco-brief-chip sxco-brief-chip--generating">Generating</span>
            )}
            {phase === "ready" && (
              <span className="sxco-brief-chip sxco-brief-chip--ready">Ready</span>
            )}
            {phase === "failed" && (
              <span className="sxco-brief-chip sxco-brief-chip--failed">Failed</span>
            )}
          </div>
          {activation && <div className="sxco-brief-actions">{activation}</div>}
        </div>

        {stamp && <p className="sxco-brief-stamp">Generated {stamp}</p>}

        {/* A request that never reached the server. The control stays available. */}
        {generate.isError && <p className="sxco-brief-error">{generate.error.message}</p>}

        {/* A failed read. With a known state on screen we keep it and say the
            latest check failed; with nothing known we say only that. */}
        {brief.isError && (
          <p className="sxco-brief-error">
            {view
              ? `Latest check failed, so this reflects the last state the server reported. ${
                  brief.error instanceof Error ? brief.error.message : ""
                }`.trim()
              : brief.error instanceof Error
                ? brief.error.message
                : "Failed to read this brief."}
          </p>
        )}

        {!companyName ? (
          <p className="sxco-brief-copy">
            A brief cannot be requested for this company because the lookup returned no company
            name, and the brief service requires one.
          </p>
        ) : rechecking ? (
          <p className="sxco-brief-copy">Checking the recorded state of this brief…</p>
        ) : (
          <>
            {phase === "idle" && (
              <p className="sxco-brief-copy">
                No brief has been requested for this company in this session. Requesting one returns
                the saved brief if this company was generated in the last 14 days. If there is none,
                a new generation starts, which calls paid APIs and takes about 3 to 4 minutes.
              </p>
            )}

            {phase === "generating" && (
              <>
                <p className="sxco-brief-copy">
                  Generation is running on the server and typically takes about 3 to 4 minutes.
                  Leaving this page does not cancel it, and returning re-checks the recorded state.
                </p>
                {elapsed && (
                  <p className="sxco-brief-meta">
                    Elapsed {elapsed} since the server recorded the request. This is elapsed time,
                    not progress.
                  </p>
                )}
              </>
            )}

            {phase === "failed" && (
              <>
                <p className="sxco-brief-copy">
                  {view?.error
                    ? "Generation failed. The provider response is reproduced below exactly as it was returned."
                    : "Generation failed and the server returned no error detail."}
                </p>
                {view?.error && <BriefErrorBody body={view.error} />}
              </>
            )}

            {confirming && (
              <div className="sxco-brief-confirm">
                <p className="sxco-brief-confirm-title">Regenerate this brief?</p>
                <p className="sxco-brief-confirm-copy">
                  Regeneration bypasses the saved brief and calls these paid APIs:
                </p>
                <ul className="sxco-brief-confirm-apis">
                  {PAID_APIS.map((api) => (
                    <li key={api}>{api}</li>
                  ))}
                </ul>
                <p className="sxco-brief-confirm-copy">
                  It takes about 3 to 4 minutes. This section returns to the generating state while
                  it runs, and the brief on screen now is replaced by the new one.
                </p>
                <div className="sxco-brief-confirm-actions">
                  <button
                    type="button"
                    className="sx-btn sx-btn-hero"
                    onClick={() => {
                      setConfirming(false);
                      request(true);
                    }}
                    disabled={busy}
                  >
                    Confirm and regenerate
                  </button>
                  <button
                    type="button"
                    className="sx-btn sx-btn-p3"
                    onClick={() => {
                      generate.reset();
                      setConfirming(false);
                    }}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {phase === "ready" && view?.body && (
              <div
                className="sxco-cib-document"
                role="document"
                aria-label={`${companyName ?? "Company"} intelligence brief`}
              >
                <header className="sxco-cib-report-head">
                  <div>
                    <p className="sxco-cib-brand">BD SIGNAL ENGINE</p>
                    <p className="sxco-cib-report-kind">Company Intelligence Brief</p>
                  </div>
                  <div className="sxco-cib-report-meta">
                    <span>Research intelligence</span>
                    {stamp && <span>Generated {stamp}</span>}
                  </div>
                  <h2>{companyName ?? "Company"}</h2>
                </header>
                <BriefDocument body={view.body} />
                <footer className="sxco-cib-report-foot">
                  <span>BD Signal Engine · Company Intelligence Brief</span>
                  <span>PDF export available</span>
                </footer>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
